import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";

interface ShippingForm {
  fullName: string;
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  phone?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "EG", name: "Egypt" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "AU", name: "Australia" },
  { code: "JP", name: "Japan" },
];

function generateOrderNumber(): string {
  const prefix = "GS";
  const timestamp = Date.now().toString(36).toUpperCase().slice(-6);
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${timestamp}-${random}`;
}

export default function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const navigate = useNavigate();
  const { items, subtotal, itemCount, clearCart } = useCart();
  const { user } = useAuth();

  const shipping = subtotal >= 50 ? 0 : 5.99;
  const tax = subtotal * 0.08;
  const total = subtotal + shipping + tax;

  const [form, setForm] = useState<ShippingForm>({
    fullName: user?.full_name || "",
    email: "",
    phone: user?.phone || "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [processing, setProcessing] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleChange = (field: keyof ShippingForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (touched[field] && errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleBlur = (field: keyof ShippingForm) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const err = validateField(field, form[field]);
    if (err) {
      setErrors((prev) => ({ ...prev, [field]: err }));
    }
  };

  const validateField = (field: keyof ShippingForm, value: string): string | undefined => {
    if (field === "addressLine2") return undefined;
    if (!value.trim()) return t("checkout.field_required");
    if (field === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return t("checkout.invalid_email");
    }
    return undefined;
  };

  const validateAll = (): boolean => {
    const requiredFields: (keyof ShippingForm)[] = [
      "fullName", "email", "phone", "addressLine1", "city", "state", "zip", "country",
    ];
    const newErrors: FormErrors = {};
    let valid = true;

    requiredFields.forEach((field) => {
      const err = validateField(field, form[field]);
      if (err) {
        newErrors[field] = err;
        valid = false;
      }
    });

    setTouched(
      requiredFields.reduce((acc, f) => ({ ...acc, [f]: true }), {})
    );
    setErrors(newErrors);
    return valid;
  };

  const handlePayNow = async () => {
    if (!validateAll()) return;

    setProcessing(true);
    await new Promise((resolve) => setTimeout(resolve, 2500));

    const orderNumber = generateOrderNumber();
    const now = new Date();
    const deliveryDate = new Date(now);
    deliveryDate.setDate(deliveryDate.getDate() + 5);

    const orderData = {
      id: `local-${Date.now()}`,
      orderNumber,
      orderDate: now.toISOString(),
      estimatedDelivery: deliveryDate.toISOString(),
      status: "processing" as const,
      items: items.map((item) => ({
        productId: item.productId,
        nameEn: item.nameEn,
        nameAr: item.nameAr,
        imageUrl: item.imageUrl,
        price: item.price,
        quantity: item.quantity,
        selectedVariants: item.selectedVariants,
      })),
      shippingAddress: {
        fullName: form.fullName,
        street: form.addressLine1 + (form.addressLine2 ? `, ${form.addressLine2}` : ""),
        city: form.city,
        state: form.state,
        zip: form.zip,
        country: form.country,
        phone: form.phone,
      },
      paymentMethod: {
        type: "Visa",
        last4: "4242",
      },
      trackingNumber: null,
      subtotal,
      shipping,
      tax,
      total,
    };

    // Save for success page
    const successData = {
      orderNumber,
      orderDate: now.toISOString(),
      estimatedDelivery: deliveryDate.toISOString(),
      items: items.map((item) => ({
        nameEn: item.nameEn,
        nameAr: item.nameAr,
        imageUrl: item.imageUrl,
        price: item.price,
        quantity: item.quantity,
        variantText: Object.entries(item.selectedVariants)
          .filter(([, v]) => v)
          .map(([, v]) => v)
          .join(" / "),
      })),
      shippingAddress: { ...form },
      subtotal,
      shipping,
      tax,
      total,
      itemCount,
    };

    localStorage.setItem("generalstore_last_order", JSON.stringify(successData));

    // Save to orders history
    try {
      const raw = localStorage.getItem("generalstore_orders");
      let orders = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(orders)) orders = [];
      orders.unshift(orderData);
      localStorage.setItem("generalstore_orders", JSON.stringify(orders));
    } catch {
      // ignore
    }

    clearCart();
    setProcessing(false);
    navigate("/checkout/success");
  };

  const formatPrice = (price: number) => `$${price.toFixed(2)}`;

  // Empty cart redirect
  if (items.length === 0 && !processing) {
    return (
      <div className="min-h-screen bg-background-50" dir={isAr ? "rtl" : "ltr"}>
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="w-full px-4 md:px-6 lg:px-8 py-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 text-sm text-foreground-500 mb-12 flex-wrap">
                <Link to="/" className="hover:text-foreground-800 transition-colors whitespace-nowrap">
                  {t("nav.home")}
                </Link>
                <span>/</span>
                <span className="text-foreground-900 font-medium">{t("checkout.title")}</span>
              </div>

              <div className="max-w-lg mx-auto text-center py-12">
                <div className="w-24 h-24 mx-auto mb-6 rounded-3xl bg-background-100 flex items-center justify-center">
                  <i className="ri-shopping-cart-line text-4xl text-foreground-300"></i>
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-3">
                  {t("cart.empty")}
                </h2>
                <p className="text-foreground-500 mb-8 leading-relaxed text-sm md:text-base">
                  {t("checkout.empty_cart")}
                </p>
                <Link
                  to="/products"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  <i className="ri-shopping-bag-line"></i>
                  {t("cart.start_shopping")}
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const inputClasses = (field: keyof ShippingForm) =>
    `w-full px-4 py-3 text-sm bg-background-100 border rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all ${
      touched[field] && errors[field] ? "border-red-300 bg-red-50/50" : "border-background-200"
    }`;

  const labelClasses = "block text-sm font-medium text-foreground-700 mb-1.5";

  return (
    <div className="min-h-screen bg-background-50" dir={isAr ? "rtl" : "ltr"}>
      <Navbar />

      {/* Processing overlay */}
      {processing && (
        <div className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-background-50 rounded-3xl px-12 py-10 shadow-lg max-w-sm w-full mx-4 text-center">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full border-4 border-primary-200 border-t-primary-500 animate-spin"></div>
            <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">
              {t("checkout.processing")}
            </h3>
            <p className="text-sm text-foreground-500">
              {t("checkout.secure_checkout")}
            </p>
          </div>
        </div>
      )}

      <main className="pt-20 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-8 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap">
              <Link to="/" className="hover:text-foreground-800 transition-colors whitespace-nowrap">
                {t("nav.home")}
              </Link>
              <span>/</span>
              <Link to="/cart" className="hover:text-foreground-800 transition-colors whitespace-nowrap">
                {t("cart.title")}
              </Link>
              <span>/</span>
              <span className="text-foreground-900 font-medium">{t("checkout.breadcrumb")}</span>
            </div>

            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {t("checkout.title")}
              </h1>
            </div>

            {/* Guest checkout indicator */}
            {!user && (
              <div className="mb-8 bg-accent-100/60 border border-accent-200/60 rounded-2xl p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-accent-200/70 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-lg text-accent-700"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-accent-800 mb-0.5">
                    {t("checkout.guest_title")}
                  </p>
                  <p className="text-sm text-accent-700/80 leading-relaxed">
                    {t("checkout.guest_message")}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <span className="text-xs text-accent-600 block mb-1.5">{t("checkout.guest_perk")}</span>
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-700 hover:text-accent-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-user-add-line"></i>
                    {t("checkout.create_account")}
                  </Link>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
              {/* Left: Shipping Form */}
              <div className="lg:col-span-2">
                <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-truck-line text-lg text-primary-500"></i>
                    </div>
                    <h2 className="text-lg font-heading font-bold text-foreground-950">
                      {t("checkout.shipping_info")}
                    </h2>
                  </div>

                  <div className="space-y-5">
                    {/* Full Name */}
                    <div>
                      <label className={labelClasses}>{t("checkout.full_name")} <span className="text-red-400">*</span></label>
                      <input
                        type="text"
                        value={form.fullName}
                        onChange={(e) => handleChange("fullName", e.target.value)}
                        onBlur={() => handleBlur("fullName")}
                        placeholder="John Doe"
                        className={inputClasses("fullName")}
                      />
                      {touched.fullName && errors.fullName && (
                        <p className="text-xs text-red-500 mt-1.5">{errors.fullName}</p>
                      )}
                    </div>

                    {/* Email + Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className={labelClasses}>{t("checkout.email")} <span className="text-red-400">*</span></label>
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          onBlur={() => handleBlur("email")}
                          placeholder="john@example.com"
                          className={inputClasses("email")}
                        />
                        {touched.email && errors.email && (
                          <p className="text-xs text-red-500 mt-1.5">{errors.email}</p>
                        )}
                      </div>
                      <div>
                        <label className={labelClasses}>{t("checkout.phone")} <span className="text-red-400">*</span></label>
                        <input
                          type="tel"
                          value={form.phone}
                          onChange={(e) => handleChange("phone", e.target.value)}
                          onBlur={() => handleBlur("phone")}
                          placeholder="+1 (555) 000-0000"
                          className={inputClasses("phone")}
                        />
                        {touched.phone && errors.phone && (
                          <p className="text-xs text-red-500 mt-1.5">{errors.phone}</p>
                        )}
                      </div>
                    </div>

                    {/* Address Line 1 */}
                    <div>
                      <label className={labelClasses}>{t("checkout.address_line1")} <span className="text-red-400">*</span></label>
                      <input
                        type="text"
                        value={form.addressLine1}
                        onChange={(e) => handleChange("addressLine1", e.target.value)}
                        onBlur={() => handleBlur("addressLine1")}
                        placeholder="123 Main Street, Apt 4B"
                        className={inputClasses("addressLine1")}
                      />
                      {touched.addressLine1 && errors.addressLine1 && (
                        <p className="text-xs text-red-500 mt-1.5">{errors.addressLine1}</p>
                      )}
                    </div>

                    {/* Address Line 2 */}
                    <div>
                      <label className={labelClasses}>{t("checkout.address_line2")}</label>
                      <input
                        type="text"
                        value={form.addressLine2}
                        onChange={(e) => handleChange("addressLine2", e.target.value)}
                        placeholder="Building name, floor, etc."
                        className={inputClasses("addressLine2")}
                      />
                    </div>

                    {/* City + State */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className={labelClasses}>{t("checkout.city")} <span className="text-red-400">*</span></label>
                        <input
                          type="text"
                          value={form.city}
                          onChange={(e) => handleChange("city", e.target.value)}
                          onBlur={() => handleBlur("city")}
                          placeholder="New York"
                          className={inputClasses("city")}
                        />
                        {touched.city && errors.city && (
                          <p className="text-xs text-red-500 mt-1.5">{errors.city}</p>
                        )}
                      </div>
                      <div>
                        <label className={labelClasses}>{t("checkout.state")} <span className="text-red-400">*</span></label>
                        <input
                          type="text"
                          value={form.state}
                          onChange={(e) => handleChange("state", e.target.value)}
                          onBlur={() => handleBlur("state")}
                          placeholder="NY"
                          className={inputClasses("state")}
                        />
                        {touched.state && errors.state && (
                          <p className="text-xs text-red-500 mt-1.5">{errors.state}</p>
                        )}
                      </div>
                    </div>

                    {/* ZIP + Country */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className={labelClasses}>{t("checkout.zip")} <span className="text-red-400">*</span></label>
                        <input
                          type="text"
                          value={form.zip}
                          onChange={(e) => handleChange("zip", e.target.value)}
                          onBlur={() => handleBlur("zip")}
                          placeholder="10001"
                          className={inputClasses("zip")}
                        />
                        {touched.zip && errors.zip && (
                          <p className="text-xs text-red-500 mt-1.5">{errors.zip}</p>
                        )}
                      </div>
                      <div>
                        <label className={labelClasses}>{t("checkout.country")} <span className="text-red-400">*</span></label>
                        <select
                          value={form.country}
                          onChange={(e) => handleChange("country", e.target.value)}
                          onBlur={() => handleBlur("country")}
                          className={`${inputClasses("country")} cursor-pointer appearance-none bg-no-repeat ${isAr ? "bg-[left_0.75rem_center]" : "bg-[right_0.75rem_center]"}`}
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                          }}
                        >
                          <option value="">{t("checkout.select_country")}</option>
                          {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        {touched.country && errors.country && (
                          <p className="text-xs text-red-500 mt-1.5">{errors.country}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Back to cart link */}
                <div className="mt-4">
                  <Link
                    to="/cart"
                    className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    {t("checkout.back_to_cart")}
                  </Link>
                </div>
              </div>

              {/* Right: Order Summary */}
              <div className="lg:col-span-1">
                <div className="sticky top-24 bg-background-50 border border-background-200 rounded-2xl p-6">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-5">
                    {t("checkout.order_summary")}
                  </h3>

                  {/* Items */}
                  <div className="space-y-4 mb-5 max-h-80 overflow-y-auto">
                    {items.map((item) => {
                      const itemTotal = item.price * item.quantity;
                      const variantText = Object.entries(item.selectedVariants)
                        .filter(([, v]) => v)
                        .map(([, v]) => v)
                        .join(" / ");

                      return (
                        <div key={item.cartKey} className="flex gap-3">
                          <div className="w-14 h-14 rounded-lg overflow-hidden bg-background-100 flex-shrink-0">
                            <img
                              src={`${item.imageUrl}&width=112&height=112`}
                              alt={isAr ? item.nameAr : item.nameEn}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug">
                              {isAr ? item.nameAr : item.nameEn}
                            </p>
                            {variantText && (
                              <p className="text-xs text-foreground-500 mt-0.5">{variantText}</p>
                            )}
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-foreground-500">Qty: {item.quantity}</span>
                              <span className="text-sm font-medium text-foreground-900">
                                {formatPrice(itemTotal)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Divider */}
                  <div className="border-t border-background-200 pt-4 space-y-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-foreground-600">
                        {t("checkout.subtotal")}
                        <span className="text-xs text-foreground-400 ml-1">
                          ({itemCount === 1 ? t("checkout.items_count", { count: itemCount }) : t("checkout.items_count_plural", { count: itemCount })})
                        </span>
                      </span>
                      <span className="font-medium text-foreground-900">{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-foreground-600">{t("checkout.shipping")}</span>
                      <span className={`font-medium ${shipping === 0 ? "text-green-600" : "text-foreground-900"}`}>
                        {shipping === 0 ? t("checkout.free") : formatPrice(shipping)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-foreground-600">{t("checkout.tax")}</span>
                      <span className="font-medium text-foreground-900">{formatPrice(tax)}</span>
                    </div>
                  </div>

                  <div className="border-t border-background-200 mt-4 pt-4">
                    <div className="flex items-center justify-between mb-6">
                      <span className="text-base font-semibold text-foreground-950">{t("checkout.total")}</span>
                      <span className="text-xl font-bold text-foreground-950">{formatPrice(total)}</span>
                    </div>

                    <button
                      onClick={handlePayNow}
                      disabled={processing}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="ri-lock-line"></i>
                      {t("checkout.pay_now")}
                    </button>
                  </div>

                  {/* Trust badge */}
                  <div className="mt-5 pt-4 border-t border-background-200">
                    <div className="flex items-center gap-2.5 text-xs text-foreground-400">
                      <i className="ri-shield-keyhole-line text-foreground-400"></i>
                      <span>{t("checkout.ssl_encrypted")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}